import React, { useState, useEffect } from 'react';
import { Card, Switch, Checkbox, Button, message, Spin, Typography, Space, Tag, Statistic, Row, Col, List, Divider } from 'antd';
import { HeartOutlined, CheckCircleOutlined, HeartFilled, GiftOutlined } from '@ant-design/icons';
import partnerService from '../../services/partnerService';
import api from '../../services/api';

const { Text, Paragraph } = Typography;

const ImpactPartnerCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [donationStats, setDonationStats] = useState(null);
  const [donationLoading, setDonationLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchDonationStats = async () => {
    try {
      setDonationLoading(true);
      const response = await api.get('/orders/partner/donations/');
      setDonationStats(response.data);
    } catch (err) {
      console.error('Failed to load donation stats:', err);
    } finally {
      setDonationLoading(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    const result = await partnerService.getImpactSettings();
    if (result.success) {
      setSettings(result.data);
      // Fetch donation stats if impact partner
      if (result.data?.is_impact_partner) {
        fetchDonationStats();
      }
    } else {
      message.error('Failed to load Impact Partner settings');
    }
    setLoading(false);
  };

  const handleToggleImpact = async (enable) => {
    if (enable && !agreeToTerms) {
      message.warning('Please agree to the Impact Partner terms first');
      return;
    }

    setSaving(true);
    const result = await partnerService.updateImpactSettings({
      is_impact_partner: enable,
      agree_to_terms: enable ? agreeToTerms : false
    });
    setSaving(false);

    if (result.success) {
      setSettings(result.data.data || result.data);
      setAgreeToTerms(false);
      message.success(enable
        ? 'You are now an Impact Partner! Thank you for your contribution.'
        : 'Impact Partner status disabled'
      );
    } else {
      message.error(result.error || 'Failed to update settings');
    }
  };

  if (loading) {
    return (
      <Card title="Impact Partner Program">
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  const isImpactPartner = settings?.is_impact_partner;

  return (
    <Card
      title={
        <Space>
          <HeartOutlined style={{ color: '#ff4d4f' }} />
          <span>Impact Partner Program</span>
          {isImpactPartner && <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>}
        </Space>
      }
      className="impact-partner-card"
    >
      {isImpactPartner ? (
        <div>
          <Paragraph>
            <Text strong style={{ color: '#52c41a' }}>
              You are an Impact Partner!
            </Text>
          </Paragraph>
          <Paragraph>
            <Text type="secondary">
              1% of all customer purchases from your store goes to the NGO they select at checkout.
              Your customers will see the Impact Partner badge next to your store.
            </Text>
          </Paragraph>

          {/* Donation Stats */}
          {donationLoading ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin size="small" />
            </div>
          ) : donationStats && parseFloat(donationStats.total_donated) > 0 ? (
            <div style={{
              background: '#fff0f6',
              border: '1px solid #ffadd2',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16
            }}>
              <Row gutter={16}>
                <Col xs={12}>
                  <Statistic
                    title="Total Contributed"
                    value={parseFloat(donationStats.total_donated).toFixed(2)}
                    prefix={<HeartFilled style={{ color: '#eb2f96' }} />}
                    suffix="€"
                    valueStyle={{ color: '#eb2f96', fontSize: 20 }}
                  />
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Orders with Donations"
                    value={donationStats.donation_count}
                    prefix={<GiftOutlined />}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
              </Row>

              {donationStats.ngo_breakdown && donationStats.ngo_breakdown.length > 0 && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>By Organization:</Text>
                  <List
                    size="small"
                    dataSource={donationStats.ngo_breakdown.slice(0, 3)}
                    renderItem={(item) => (
                      <List.Item
                        style={{ padding: '4px 0' }}
                        extra={<Tag color="pink">€{parseFloat(item.total_donated).toFixed(2)}</Tag>}
                      >
                        <Space>
                          <HeartOutlined style={{ color: '#eb2f96' }} />
                          <span>{item.ngo_name}</span>
                        </Space>
                      </List.Item>
                    )}
                  />
                </>
              )}
            </div>
          ) : (
            <div style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
              textAlign: 'center'
            }}>
              <Text type="secondary">
                No donations yet. When customers choose NGOs at checkout, contributions will appear here.
              </Text>
            </div>
          )}

          <Paragraph>
            <Text type="secondary">
              Joined: {settings.impact_agreed_at
                ? new Date(settings.impact_agreed_at).toLocaleDateString()
                : 'N/A'}
            </Text>
          </Paragraph>
          <Button
            danger
            onClick={() => handleToggleImpact(false)}
            loading={saving}
          >
            Disable Impact Partner
          </Button>
        </div>
      ) : (
        <div>
          <Paragraph>
            <Text>
              Join the Impact Partner program and contribute to animal welfare.
              As an Impact Partner, 1% of all customer purchases from your store
              will be donated to the NGO selected by the customer.
            </Text>
          </Paragraph>

          <div style={{
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 8,
            padding: 16,
            marginBottom: 16
          }}>
            <Checkbox
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
            >
              <Text>
                I agree to donate <strong>1% of total sales</strong> to the NGO
                selected by customers at checkout.
              </Text>
            </Checkbox>
          </div>

          <Button
            type="primary"
            icon={<HeartOutlined />}
            onClick={() => handleToggleImpact(true)}
            loading={saving}
            disabled={!agreeToTerms}
          >
            Become an Impact Partner
          </Button>
        </div>
      )}
    </Card>
  );
};

export default ImpactPartnerCard;
